import { Check, ClipboardCopy, Info } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaTextarea } from "@/shared/components/forms/ColocaTextarea";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { useCopyToClipboard } from "@/shared/hooks/useCopyToClipboard";
import useTranslations from "@/shared/lang/useTranslations";

import type { PaymentIncidentReport } from "../types/paymentIncidents.types";

export interface IncidentReportDialogProps {
  report: PaymentIncidentReport | null;
  provider: string;
  onClose: () => void;
}

/**
 * The report, ready to paste in the provider's channel. It is copied as it opens; a browser that refuses the
 * clipboard after the second factor (Safari ties it to the click) still has the text on screen and a button.
 */
export function IncidentReportDialog({ report, provider, onClose }: IncidentReportDialogProps) {
  const { t } = useTranslations("paymentIncidents");
  const { copiedValue, copy } = useCopyToClipboard(t("report_copy_error"));
  const autoCopy = useCopyToClipboard().copy;
  const attempted = useRef<string | null>(null);
  // The text the clipboard took as the dialog opened; it stays said, unlike the button's two-second "copied".
  const [autoCopiedText, setAutoCopiedText] = useState<string | null>(null);
  const text = report?.text ?? "";
  const copied = !!text && copiedValue === text;
  const autoCopied = !!text && autoCopiedText === text;
  // The dialog stays mounted between opens: a follow-up with the same text must be copied, and said, again.
  if (!report && autoCopiedText !== null) setAutoCopiedText(null);

  useEffect(() => {
    if (!text) {
      attempted.current = null;
      return;
    }
    if (attempted.current === text) return;
    attempted.current = text;
    void autoCopy(text).then((ok) => {
      if (ok) setAutoCopiedText(text);
    });
  }, [autoCopy, text]);

  return (
    <ColocaDialog
      open={report !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="xl"
      title={t("report_title", { provider })}
      description={t("report_description", { provider })}
      footer={
        <>
          <ColocaButton variant="ghost" onClick={onClose}>
            {t("action_close")}
          </ColocaButton>
          <ColocaButton
            variant="primary"
            icon={copied ? Check : ClipboardCopy}
            onClick={() => void copy(text, t("report_copied_toast"))}
          >
            {copied ? t("report_copied") : autoCopied ? t("report_copy_again") : t("report_copy")}
          </ColocaButton>
        </>
      }
    >
      <div className="space-y-3">
        {report?.evidence_source !== "live" && (
          <ColocaAlert
            variant={report?.evidence_source === "stored" ? "secondary" : "warning"}
            icon={Info}
            text={t(
              report?.evidence_source === "stored" ? "report_evidence_stored" : "report_evidence_none",
              {
                provider,
              },
            )}
          />
        )}
        <ColocaTextarea
          value={text}
          onValueChange={() => undefined}
          readOnly
          rows={16}
          aria-label={t("report_text_label")}
          className="font-mono text-xs leading-relaxed"
          onFocus={(event) => event.currentTarget.select()}
        />
        <p className="text-xs text-muted-foreground">
          {autoCopied ? t("report_auto_copied", { provider }) : t("report_copy_hint", { provider })}
        </p>
      </div>
    </ColocaDialog>
  );
}
